import {
  beginApiRequest,
  finishApiRequest,
  parseServerTiming,
} from "./devDiagnostics";

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL || "/api";

type ApiEnvelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
  errors?: ApiErrorItem[];
};

type ApiErrorItem = {
  field?: string | null;
  message?: string | null;
};

export class ApiHttpError extends Error {
  readonly payload: unknown;
  readonly status: number;

  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.name = "ApiHttpError";
    this.status = status;
    this.payload = payload;
  }
}

export function isApiHttpError(error: unknown): error is ApiHttpError {
  return error instanceof ApiHttpError;
}

function formatApiError<T>(payload: ApiEnvelope<T> | T): string {
  if (!payload || typeof payload !== "object") {
    return "Request failed.";
  }

  const envelope = payload as ApiEnvelope<T>;
  const message = envelope.message ?? "Request failed.";

  if (!Array.isArray(envelope.errors) || envelope.errors.length === 0) {
    return message;
  }

  const details = envelope.errors
    .map((error) => {
      const field = error.field?.trim();
      const errorMessage = error.message?.trim();

      if (!errorMessage) {
        return null;
      }

      return field ? `${field}: ${errorMessage}` : errorMessage;
    })
    .filter((detail): detail is string => detail !== null);

  return details.length > 0 ? `${message} ${details.join(" ")}` : message;
}

function buildHeaders(options: RequestInit): Headers {
  const headers = new Headers(options.headers);

  if (!headers.has("Content-Type") && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  return headers;
}

export async function orvalFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const method = options.method ?? "GET";
  const startedAt = typeof performance === "undefined" ? Date.now() : performance.now();
  const diagnosticId = beginApiRequest(method, url);
  let response: Response | null = null;
  let requestSucceeded = false;

  try {
    response = await fetch(`${API_BASE}${url}`, {
      credentials: "include",
      ...options,
      headers: buildHeaders(options),
    });

    const payload = (await response.json().catch(() => ({}))) as ApiEnvelope<T> | T;
    const isEnvelope = payload && typeof payload === "object" && "success" in payload;

    if (!response.ok || (isEnvelope && (payload as ApiEnvelope<T>).success === false)) {
      throw new ApiHttpError(response.status, formatApiError(payload), payload);
    }

    requestSucceeded = true;

    return (isEnvelope ? (payload as ApiEnvelope<T>).data : payload) as T;
  } finally {
    finishApiRequest(diagnosticId, startedAt, {
      status: requestSucceeded ? "success" : "error",
      statusCode: response?.status ?? null,
      profilerUrl: response?.headers.get("x-webdo-symfony-profiler-url")
        ?? response?.headers.get("x-debug-token-link")
        ?? null,
      serverTiming: parseServerTiming(response?.headers.get("server-timing") ?? null),
    });
  }
}
